// Product Gallery Controller
// Drives a native scroll-snap carousel for the main product image.
// - Mobile: users swipe the main image directly (native touch scroll + snap).
// - Desktop: prev/next arrows and left/right keyboard keys scroll the track.
// Thumbnails scroll the carousel to the chosen image, and the active thumbnail
// stays highlighted as the user swipes (derived from the track's scroll position).
// Used on the public product show page.
import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["track", "slide", "thumbnail"]

  connect() {
    this.activeIndex = 0
    this.markActive(0)
    this.boundHandleKey = this.handleKey.bind(this)
    document.addEventListener("keydown", this.boundHandleKey)
  }

  disconnect() {
    document.removeEventListener("keydown", this.boundHandleKey)
    if (this.scrollFrame) window.cancelAnimationFrame(this.scrollFrame)
  }

  get count() {
    return this.slideTargets.length
  }

  // Thumbnail click -> scroll the carousel to that image.
  select(event) {
    const index = this.thumbnailTargets.indexOf(event.currentTarget)
    if (index === -1) return
    this.scrollToIndex(index)
  }

  next() {
    if (this.count === 0) return
    this.scrollToIndex((this.activeIndex + 1) % this.count)
  }

  prev() {
    if (this.count === 0) return
    this.scrollToIndex((this.activeIndex - 1 + this.count) % this.count)
  }

  scrollToIndex(index) {
    const slide = this.slideTargets[index]
    if (!slide || !this.hasTrackTarget) return
    this.trackTarget.scrollTo({ left: slide.offsetLeft, behavior: "smooth" })
    // Update immediately for arrow/keyboard nav; onScroll confirms once it settles.
    this.setActive(index)
  }

  // Native scroll (swipe or programmatic) -> figure out which slide is in view.
  onScroll() {
    if (this.scrollFrame) window.cancelAnimationFrame(this.scrollFrame)
    this.scrollFrame = window.requestAnimationFrame(() => {
      const width = this.trackTarget.clientWidth
      if (!width) return
      const index = Math.round(this.trackTarget.scrollLeft / width)
      this.setActive(index)
    })
  }

  setActive(index) {
    const clamped = Math.max(0, Math.min(index, this.count - 1))
    this.activeIndex = clamped
    this.markActive(clamped)
  }

  markActive(index) {
    this.thumbnailTargets.forEach((t, i) => {
      const isActive = i === index
      t.classList.toggle("border-primary", isActive)
      t.classList.toggle("border-transparent", !isActive)
      t.setAttribute("aria-current", isActive ? "true" : "false")
    })
  }

  handleKey(event) {
    // Ignore keys when typing in inputs
    const tag = (event.target.tagName || "").toLowerCase()
    if (tag === "input" || tag === "textarea" || event.target.isContentEditable) return

    if (event.key === "ArrowRight") {
      event.preventDefault()
      this.next()
    } else if (event.key === "ArrowLeft") {
      event.preventDefault()
      this.prev()
    }
  }
}
